import React, { Suspense, lazy, memo } from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-10 w-10 border-3 border-[#003366] border-t-transparent"></div>
      <p className="text-sm text-slate-500">Loading...</p>
    </div>
  </div>
);

// Lazy load pages for better performance
const LandingPage = lazy(() => import("@/pages/LandingPage").then(m => ({ default: m.LandingPage })));
const LoginPage = lazy(() => import("@/pages/AuthPages").then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import("@/pages/AuthPages").then(m => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage").then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage").then(m => ({ default: m.ResetPasswordPage })));
const MarketplacePage = lazy(() => import("@/pages/MarketplacePage"));
const BookDetailPage = lazy(() => import("@/pages/BookDetailPage").then(m => ({ default: m.BookDetailPage })));
const CreateListingPage = lazy(() => import("@/pages/CreateListingPage").then(m => ({ default: m.CreateListingPage })));
const EditListingPage = lazy(() => import("@/pages/EditListingPage").then(m => ({ default: m.EditListingPage })));
const ProfilePage = lazy(() => import("@/pages/ProfilePage").then(m => ({ default: m.ProfilePage })));
const TransactionSuccessPage = lazy(() => import("@/pages/TransactionSuccessPage").then(m => ({ default: m.TransactionSuccessPage })));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard").then(m => ({ default: m.AdminDashboard })));
const InfoPage = lazy(() => import("@/pages/InfoPage").then(m => ({ default: m.InfoPage })));
const PolicyPage = lazy(() => import("@/pages/PolicyPage").then(m => ({ default: m.PolicyPage })));
const SellerGuidePage = lazy(() => import("@/pages/SellerGuidePage").then(m => ({ default: m.SellerGuidePage })));

// Protected Route Component
const ProtectedRoute = memo(({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (adminOnly && !user.is_admin) {
    return <Navigate to="/" replace />;
  }
  
  return children;
});

// Layout component with Header - memoized to prevent re-renders
const Layout = memo(({ children, showHeader = true }) => {
  return (
    <div className="min-h-screen flex flex-col page-transition">
      {showHeader && <Header />}
      <main className="flex-1 pb-safe">{children}</main>
    </div>
  );
});

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Layout showHeader={false}><LandingPage /></Layout>} />
      <Route path="/login" element={<Layout showHeader={false}><LoginPage /></Layout>} />
      <Route path="/register" element={<Layout showHeader={false}><RegisterPage /></Layout>} />
      <Route path="/forgot-password" element={<Layout showHeader={false}><ForgotPasswordPage /></Layout>} />
      <Route path="/reset-password" element={<Layout showHeader={false}><ResetPasswordPage /></Layout>} />
      <Route path="/marketplace" element={<Layout><MarketplacePage /></Layout>} />
      <Route path="/book/:id" element={<Layout><BookDetailPage /></Layout>} />
      <Route path="/info" element={<Layout><InfoPage /></Layout>} />
      <Route path="/policy" element={<Layout><PolicyPage /></Layout>} />
      <Route path="/seller-guide" element={<Layout><SellerGuidePage /></Layout>} />
      
      {/* Protected Routes */}
      <Route path="/sell" element={
        <ProtectedRoute>
          <Layout><CreateListingPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/edit-book/:id" element={
        <ProtectedRoute>
          <Layout><EditListingPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Layout><ProfilePage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/transaction-success" element={
        <ProtectedRoute>
          <Layout showHeader={false}><TransactionSuccessPage /></Layout>
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute adminOnly>
          <Layout><AdminDashboard /></Layout>
        </ProtectedRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
